import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Chip, Theme } from "@mui/material";

import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import { ChangeKind, parseChangelog, parseVersions } from "../changelog";
import { KIND_ORDER, KIND_STYLE } from "./ChangelogVersionView";

interface ChangelogSidebarProps {
  /** The changelog Markdown. */
  content: string;
  /** Version to start on (the new-version banner opens the docs on it); the newest by default */
  initialVersion?: string;
}

const titleSx = (theme: Theme) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  mb: 1.5
});

const itemSx = (active: boolean) => (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.25,
  py: 0.75,
  borderRadius: "8px",
  cursor: "pointer",
  mb: 0.5,
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: active ? `${theme.palette.primary.main}10` : "transparent",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover
  }
});

const countSx = {
  fontSize: 11,
  fontWeight: 700,
  borderRadius: "10px",
  px: "7px",
  lineHeight: "18px"
};

/**
 * Version list for the changelog tab, one compact line per version with its counts per kind of change. Clicking a version broadcasts a
 * `changelog-version-select` event consumed by the content pane.
 */
const ChangelogSidebar: React.FC<ChangelogSidebarProps> = ({ content, initialVersion }) => {
  const { t } = useTranslation();
  const versions = parseVersions(content);
  const [activeVersion, setActiveVersion] = useState<string>(
    (initialVersion && versions.some((entry) => entry.version === initialVersion) ? initialVersion : versions[0]?.version) || ""
  );
  // items per kind of change, so each version shows at a glance what it brought
  const countsByVersion = useMemo(() => {
    const result: Record<string, { kind: ChangeKind; count: number }[]> = {};

    parseChangelog(content).forEach((entry) => {
      result[entry.version] = KIND_ORDER.map((kind) => ({
        kind,
        count: entry.sections.filter((s) => s.kind === kind).reduce((sum, s) => sum + s.items.length, 0)
      })).filter((k) => k.count > 0);
    });

    return result;
  }, [content]);

  useEffect(() => {
    if (versions.length > 0 && !activeVersion) {
      setActiveVersion(versions[0].version);
    }
  }, [versions, activeVersion]);

  const handleClick = (version: string) => {
    setActiveVersion(version);
    window.dispatchEvent(new CustomEvent("changelog-version-select", { detail: version }));
  };

  if (versions.length === 0) return null;

  return (
    <div>
      <Typography sx={titleSx}>{t(CustomerMessages.docsVersionsTitle)}</Typography>
      {versions.map((entry) => {
        const active = activeVersion === entry.version;

        return (
          <Box key={entry.version} sx={itemSx(active)} onClick={() => handleClick(entry.version)}>
            <Chip label={`v${entry.version}`} size="small" color={active ? "primary" : "default"} sx={{ fontWeight: 700 }} />
            <Box sx={(theme) => ({ flex: 1, fontSize: 12, color: theme.palette.text.disabled })}>{entry.date}</Box>
            {(countsByVersion[entry.version] || []).length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {countsByVersion[entry.version].map(({ kind, count }) => (
                  <Box
                    component="span"
                    key={kind}
                    sx={{ ...countSx, color: KIND_STYLE[kind].color, backgroundColor: KIND_STYLE[kind].background }}
                    title={`${count} ${t(count === 1 ? KIND_STYLE[kind].one : KIND_STYLE[kind].many)}`}
                  >
                    {count}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </div>
  );
};

ChangelogSidebar.defaultProps = {
  initialVersion: undefined
};

export default ChangelogSidebar;
